import clsx from "clsx"
import { useTranslation } from "react-i18next"
import { useAtomValue } from "jotai"
import Projects from "./Projects"
import Tutorials from "./Tutorials"
import { Icon } from "./icons/Icon"
import { IconName } from "./icons/icons"
import { homeSectionAtom } from "@/store/ui-views-state"
import { Account } from "./Account"
import { UserProfile } from "./ui/UserProfile"

import { useUserQuery } from "@/api/users-api"
import { HomePageSection } from "@/store/undoable-state"
import { Button } from "./ui/Button"
import { capitalized } from "@/utils/string"
import { useEffect, useLayoutEffect, useMemo } from "react"
import {
	markOnboardingShown,
	showingIntakeSurveyDialogAtom,
	showingSubscriptionDialogAtom,
	showingWelcomeDialogAtom,
} from "@/store/app-state"
import { IntakeSurveyDialog } from "./Dialogs/IntakeSurveyDialog"
import { replaceDialog, resetDialogs } from "@/store/dialogs-state"
import { analytics } from "@/telemetry/amplitude"
import { ConfirmDialog } from "./Dialogs/ConfirmDialog"
import { SubscriptionsDialog } from "./Dialogs/SubscriptionsDialog"
import { isElectronApp, notifyAppReady } from "@/utils/electron"
import { useAuth } from "@/utils/auth"
import AIStyles from "./AIStyles"
import { Settings } from "./Settings/SettingsPage"
import { getPathForHomeSection } from "@/utils/home-sections"
import { routes } from "@/utils/routes"
import { ProductSwitcher } from "./ProductSwitcher"
import { getProductName } from "@/utils/product-name"
import VideoPlayer from "./VideoPlayer"
import { migrateOnboardingStateFromLocalStorage } from "@/store/onboarding/onboarding-service"
import { isPixiesetAuthEnabled } from "@/utils/measurement/feature-flag"
import { HelpCenter } from "./HelpCenter"
import { getOS } from "@/utils/system"
import { getDesktopAppDownloadUrl } from "@/utils/polarr-urls"
import { formatNumber } from "@/utils/number"
import { activePolarrSubscription } from "@/api/user-helpers"
import { pixiesetAssetBaseURIs } from "@/utils/pixieset-urls"

let hasShownSunsetDialogThisSession = false

const showSubscriptionDialog = () => {
	replaceDialog(SubscriptionsDialog)
	window.location.hash = ""
}

function Home() {
	const { t } = useTranslation("components")
	const selectedSection = useAtomValue(homeSectionAtom)
	const shouldShowWelcomeDialog = useAtomValue(showingWelcomeDialogAtom)
	const shouldShowSubscriptionDialog = useAtomValue(showingSubscriptionDialogAtom)
	const shouldShowIntakeSurveyDialog = useAtomValue(showingIntakeSurveyDialogAtom)
	const navigateHome = routes.root.useNavigate()
	const navigateStyles = routes.styles.useNavigate()
	const navigateTutorials = routes.tutorials.useNavigate()
	const navigateSupport = routes.support.useNavigate()
	const navigateSettings = routes.settings.useNavigate()
	const navigateAccount = routes.account.useNavigate()

	const navigateByPath = useMemo(
		() => ({
			[routes.root.build()]: navigateHome,
			[routes.styles.build()]: navigateStyles,
			[routes.tutorials.build()]: navigateTutorials,
			[routes.support.build()]: navigateSupport,
			[routes.settings.build()]: navigateSettings,
			[routes.account.build()]: navigateAccount,
		}),
		[
			navigateAccount,
			navigateHome,
			navigateSettings,
			navigateStyles,
			navigateSupport,
			navigateTutorials,
		],
	)

	const userQuery = useUserQuery()
	const user = userQuery.data
	const isPixiesetAuth = isPixiesetAuthEnabled()
	const shouldShowSunsetDialog = !isPixiesetAuth
	const onboardingWelcomeVimeoUrl = isPixiesetAuth
		? "https://player.vimeo.com/video/1170927563?badge=0&autopause=0&player_id=0&app_id=58479"
		: undefined
	const onboardingWelcomeYoutubeId = isPixiesetAuth ? undefined : "QiYvIC7ReSE"
	const onboardingWelcomeHlsUrl = isPixiesetAuth
		? "https://files-pe.pixieset.com/photoeditor/tutorials/onboarding/m3u8/4K%20Final%20V.6%20-%20Intro%20Photo%20Editor%20-%20Pixieset.m3u8"
		: `${pixiesetAssetBaseURIs.filesPe}/pn/videos/Quick_Tour__One_App_for_AI_Editing_and_Culling___Polarr_Next.m3u8`
	const onboardingWelcomePosterUrl = isPixiesetAuth
		? "https://files-pe.pixieset.com/photoeditor/tutorials/onboarding/Thumbnail/V.2%20Final%20Thumbnail%20-%20Intro%20Video.jpg"
		: undefined
	const onboardingWelcomeTitle = t("home.welcomeTitle", { productName: getProductName() })

	const desktopDownloadTrackingValues = {
		userId: user?.userId ?? null,
		OS: getOS(),
	}

	useLayoutEffect(() => {
		notifyAppReady(APP_VERSION)
	}, [])

	// Attempt localStorage migration when user is loaded
	useEffect(() => {
		if (user) {
			migrateOnboardingStateFromLocalStorage().catch(console.error)
		}
	}, [user])

	useEffect(() => {
		if (shouldShowSunsetDialog) {
			return
		}
		if (shouldShowSubscriptionDialog) {
			showSubscriptionDialog()
		}
	}, [shouldShowSubscriptionDialog, shouldShowSunsetDialog])

	useEffect(() => {
		if (shouldShowSunsetDialog) {
			return
		}
		if (shouldShowWelcomeDialog) {
			replaceDialog(ConfirmDialog, {
				trackingViewName: "welcome_dialog",
				className: "p-1 w-full !space-y-2 !mt-0",
				contentSize: "md-h-auto" as const,
				embeddedComponent: (
					<>
						<div className="text-xl font-bold">
							{t("home.welcomeTitle", { productName: getProductName() })}
						</div>
						<div className="pb-4 text-sm text-base-400">
							{t("home.welcomeDescription")}
						</div>
						<div className="relative aspect-video w-full shrink-0 overflow-hidden rounded-lg bg-base-900">
							<VideoPlayer
								title={onboardingWelcomeTitle}
								vimeoUrl={onboardingWelcomeVimeoUrl}
								youtubeId={onboardingWelcomeYoutubeId}
								hlsUrl={onboardingWelcomeHlsUrl}
								posterUrl={onboardingWelcomePosterUrl}
								onPlay={(target) => {
									analytics.trackEvent(
										{
											label: "play_tutorial",
											type: "click",
											view: onboardingWelcomeTitle,
											customProperties: {
												origin: "onboarding",
											},
										},
										target,
									)
								}}
							/>
						</div>
					</>
				),
				primary: {
					title: t("home.continue"),
				},
				canClose: false,
				onFinish: () => {
					markOnboardingShown("welcomeShown")
				},
			})
		}
	}, [
		shouldShowWelcomeDialog,
		onboardingWelcomeHlsUrl,
		onboardingWelcomeTitle,
		onboardingWelcomeVimeoUrl,
		onboardingWelcomeYoutubeId,
		onboardingWelcomePosterUrl,
		shouldShowSunsetDialog,
	])

	useEffect(() => {
		if (shouldShowSunsetDialog) {
			return
		}
		if (shouldShowIntakeSurveyDialog) {
			replaceDialog(IntakeSurveyDialog, {
				resultHandler: (result) => {
					analytics.setUserProperties(result)
					markOnboardingShown("intakeSurveyShown")
				},
			})
		}
	}, [shouldShowIntakeSurveyDialog, shouldShowSunsetDialog])

	useEffect(() => {
		if (!shouldShowSunsetDialog) {
			return
		}

		if (!user) {
			return
		}

		if (hasShownSunsetDialogThisSession) {
			return
		}
		hasShownSunsetDialogThisSession = true

		const hasActiveSubscription = activePolarrSubscription(user) !== null
		const message = hasActiveSubscription
			? t("home.sunsetMessageActive")
			: t("home.sunsetMessageInactive")
		const url = hasActiveSubscription
			? "https://website-help.pixieset.com/en/articles/14603013"
			: "https://website-help.pixieset.com/en/articles/14603148"
		resetDialogs()
		replaceDialog(ConfirmDialog, {
			trackingViewName: "polarr_next_shutdown_dialog",
			title: t("home.sunsetTitle"),
			message,
			secondary: {
				title: t("common:action.ok"),
			},
			primary: {
				title: t("home.learnMore"),
				handler: () => {
					window.open(url, "_blank", "noopener,noreferrer")
				},
			},
		})
	}, [shouldShowSunsetDialog, user])

	const navigateToSection = (section: HomePageSection) => {
		const targetPath = getPathForHomeSection(section)
		const navigateToPath = navigateByPath[targetPath] ?? navigateHome
		void navigateToPath()
	}

	const renderSection = (section: HomePageSection, icon: IconName | null, title?: string) => (
		<Button
			variant="custom"
			size="custom"
			trackingLabel={`open_${section}`}
			className={clsx(
				selectedSection === section ? "text-base-200" : "text-base-500 hover:text-base-200",
				"flex w-fit flex-row space-x-2 font-medium",
			)}
			onClick={() => navigateToSection(section)}
		>
			{!icon && <div className="size-5"></div>}
			{icon && <Icon name={icon} size={20} />}
			<div className="flex-1 whitespace-nowrap">{title ?? capitalized(section)}</div>
		</Button>
	)

	return (
		<div aria-label={t("home.ariaHome")} className="flex w-full flex-row">
			<div
				className={`flex w-[248px] flex-col justify-between bg-base-800 p-8 pr-4 ${isPixiesetAuthEnabled() ? "mt-0.5 pt-5" : ""}`}
			>
				<div className="flex w-fit flex-col justify-between space-y-8 text-sm">
					<div aria-hidden className="mb-2 flex items-center justify-between">
						<div className="flex">
							<ProductSwitcher />
							<div className="ml-[6px] flex items-center rounded-[100px] bg-base-700 px-2 text-[10px] font-medium text-base-400">
								{t("home.beta")}
							</div>
						</div>
						{isPixiesetAuthEnabled() && <HelpCenter />}
					</div>
					{renderSection("projects", "projects", t("home.navProjects"))}
					{renderSection("ai_styles", "style", t("home.navAiStyles"))}
					{renderSection("tutorials", "tutorials", t("home.navTutorials"))}
					{renderSection("settings", "settings", t("home.navSettings"))}
				</div>
				<div className="flex flex-col items-start space-y-6">
					{!isElectronApp && (
						<Button
							trackingLabel="open_desktop_app_download"
							trackingValues={desktopDownloadTrackingValues}
							variant="custom"
							size="custom"
							className="flex flex-row space-x-1 rounded-md border border-base-600 bg-base-700 px-4 py-2 text-sm text-base-100 hover:bg-base-600 hover:text-base-50"
							onClick={() => {
								const downloadUrl = getDesktopAppDownloadUrl(isPixiesetAuth)
								window.open(downloadUrl, "_blank", "noopener,noreferrer")
							}}
						>
							<Icon name="install_desktop" size={20} />
							<b>{t("home.installDesktopApp")}</b>
						</Button>
					)}
					<div className="w-full min-w-0" onClick={() => navigateToSection("my_account")}>
						<UserInfo />
					</div>
				</div>
			</div>

			<div className="flex size-full flex-1 bg-base-900" style={{ minWidth: 0 }}>
				{selectedSection == "projects" && <Projects />}
				{selectedSection == "ai_styles" && <AIStyles />}
				{selectedSection == "tutorials" && <Tutorials />}
				{selectedSection === "my_account" && <Account />}
				{selectedSection === "settings" && <Settings />}
			</div>
		</div>
	)
}

const UserInfo = () => {
	const { t } = useTranslation("components")
	const userQuery = useUserQuery()
	const { logout } = useAuth()

	if (userQuery.isError) {
		return (
			<Button
				trackingLabel="log_out"
				size="sm"
				className="w-fit p-2"
				onClick={() => void logout()}
			>
				{t("home.logOut")}
			</Button>
		)
	}

	if (!userQuery.data) {
		return null
	}

	const isPixiesetAuth = isPixiesetAuthEnabled()
	const displayName = isPixiesetAuth
		? userQuery.data.businessName?.trim() || userQuery.data.displayName
		: userQuery.data.displayName

	return (
		<div
			role="button"
			aria-label={t("home.ariaAccount")}
			className="flex w-full min-w-0 flex-row items-center space-x-4 hover:text-base-50"
		>
			<div className="size-[40px] shrink-0">
				<UserProfile user={userQuery.data} size="small" />
			</div>
			<div className="flex min-w-0 flex-1 flex-col space-y-1 text-base-200">
				<div className="text-md" style={{ wordBreak: "break-word" }}>
					{displayName}
				</div>
				{isPixiesetAuth && (
					<div className="truncate text-xs text-base-300">{userQuery.data.email}</div>
				)}
				<div className="text-xs text-base-300">
					{userQuery.data.hasUnlimitedExports
						? t("home.unlimited")
						: t("home.userExports", {
								count: formatNumber(userQuery.data.totalCreditsAvailable),
							})}
				</div>
			</div>
		</div>
	)
}
export default Home
